import React, { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { certificateActions, alertActions } from '../../../store/actions'

/**
 * PCC 본인확인 V4 (PC) 컴포넌트.
 *
 * V2/V3(Pcc.jsx)와 병행 운영하는 추가(additive) 구현.
 * 차이점:
 *  - 파라미터 조회를 openPccRequestV4(memberId)로 호출 → reqNum이 member_id 기반 유니크 값
 *  - 표준창 action이 pcc_V3_j10_v4.jsp (응답의 standardWindowUrl 사용)
 *  - hidden form에 crypto_token_id / integrity_value 추가
 *  - 완료 시 retInfo 대신 crypto_token_id를 resultPccInfoV4에 전달
 *
 * props.memberId (옵션): 회원가입 흐름에서 입력한 아이디. 미전달 시 서버가 로그인 사용자/익명 처리.
 */
const PccV4 = (props) => {
  // 한 번의 본인인증(open 사이클)당 팝업을 한 번만, 그리고 '새로 받은' reqInfo로만 열기 위한 가드.
  //  - armed:    이번 사이클이 활성인지(본인인증 클릭 후 ~ 팝업/닫힘 전)
  //  - sawClear: setInitialState로 reqInfo가 ''로 초기화된 것을 관측했는지
  //              (관측 전의 truthy reqInfo는 이전 사이클의 잔존값이므로 무시)
  //  - opened:   이번 사이클에서 이미 팝업을 열었는지
  const cycleRef = useRef({ armed: false, sawClear: false, opened: false })

  // 리스너 등록
  useEffect(() => {
    window.addEventListener('message', pccMessageFunc, false)
  }, [])

  // 리스너 해제 (미해제 시 윈도우에 계속 남아 과부하 발생)
  useEffect(
    () => () => {
      window.removeEventListener('message', pccMessageFunc, false)
    },
    [],
  )

  const pccMessageFunc = (e) => {
    if (e.data.message) {
      switch (e.data.message) {
        case 'pccWindowClose':
          props.onChange()
          break
        case 'pccComplete':
          // V4: 표준창이 회신한 crypto_token_id로 결과 조회
          props.resultPccInfoV4(document.getElementById('retCryptoTokenId').value)
          break
        default:
          break
      }
    }
  }

  // 본인인증 클릭(isPccOpen=true) 시점에: 이전 값 초기화 후, 그때의 memberId로 파라미터 요청.
  // (마운트 시점 조회 금지 — 진입만으로 /cert/pcc-param-v4가 호출되고 memberId 없이 ANON reqNum이 생성됨)
  // isPccOpen이 false로 돌아오면 다음 사이클을 위해 가드를 리셋.
  useEffect(() => {
    if (props.isPccOpen === true) {
      cycleRef.current = { armed: true, sawClear: false, opened: false }
      props.setInitialState() // reqInfo='' 로 초기화 → 이전(stale) 값으로 오픈되는 것 방지
      props.openPccRequestV4(props.memberId)
    } else {
      cycleRef.current = { armed: false, sawClear: false, opened: false }
    }
  }, [props.isPccOpen])

  useEffect(() => {
    if (props.certResult === 'Y') {
      props.success({
        title: '확인',
        message: '본인인증이 정상 인증되었습니다.',
        params: {
          type: 'success',
          callback: () => {
            props.onChange()
            props.callBack && props.callBack(true)
          },
        },
      })
    } else if (props.certResult === 'N') {
      props.onChange()
      props.error({
        title: '확인',
        message: '본인인증을 실패했습니다.',
      })
    }
  }, [props.certResult])

  // 새로 받은 reqInfo가 준비된 뒤에만 팝업 오픈.
  useEffect(() => {
    const c = cycleRef.current
    if (!c.armed || c.opened) return
    if (!props.pccInfo.reqInfo) {
      // 초기화(reqInfo='')가 반영된 것을 관측 → 이후 도착하는 값만 '새 값'으로 인정
      c.sawClear = true
      return
    }
    // 아직 초기화를 관측하지 못한 상태의 truthy 값은 이전 사이클의 잔존값이므로 무시
    if (!c.sawClear) return
    c.opened = true
    window.open(
      '/pccCertPopupV4.htm',
      'PCCV3Window',
      'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=no, copyhistory=no, width=415,height=600',
    )
  }, [props.isPccOpen, props.pccInfo.reqInfo])

  return (
    <React.Fragment>
      <form
        id="tranMgr"
        target="PCCV3Window"
        name="PCCV3WFRM"
        method="post"
        action={props.pccInfo.standardWindowUrl || 'https://pcc.siren24.com/pcc_V3/jsp/pcc_V3_j10_v4.jsp'}
      >
        <input type="hidden" id="verSion" name="verSion" value={props.pccInfo.verSion || '3'} readOnly />
        <input type="hidden" id="retUrl" name="retUrl" value={props.pccInfo.retUrl} readOnly />
        <input type="hidden" id="reqInfo" name="reqInfo" value={props.pccInfo.reqInfo} readOnly />
        <input type="hidden" id="crypto_token_id" name="crypto_token_id" value={props.pccInfo.cryptoTokenId} readOnly />
        {/*<input type="hidden" id="integrity_value" name="integrity_value" value={props.pccInfo.integrityValue} readOnly />*/}
        {/* 표준창이 회신하는 crypto_token_id 수신용 */}
        <input type="hidden" id="retCryptoTokenId" name="retCryptoTokenId" />
      </form>
    </React.Fragment>
  )
}

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    pccInfo: state.certificate,
    certResult: state.certificate?.resultInfo?.result,
  }
}

const actionCreators = {
  openPccRequestV4: certificateActions.openPccRequestV4,
  resultPccInfoV4: certificateActions.resultPccInfoV4,
  setInitialState: certificateActions.setInitialState,
  success: alertActions.success,
  error: alertActions.error,
}

export default connect(mapStateToProps, actionCreators)(PccV4)
